import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { AuthCard } from "./AuthCard";
import { usePortalApi } from "./usePortalApi";
import { usePortalAuthConfig } from "./usePortalAuthConfig";

// Generic success copy: the backend returns 200 for any email, so the response
// never reveals whether an account exists.
const GENERIC_SUCCESS = "If an account exists for that email, we've sent a password-reset link.";

/**
 * Forgot-password screen (`/forgot-password`).
 *
 * A single email field POSTs `<apiPrefix>/auth/forgot-password`, with the
 * config's `authExtra` merged in (the GPO portal's tenant `slug`, so the reset
 * link targets that GPO's host). The endpoint always returns 200, so the screen
 * shows the same generic success message. A transport error shows that message
 * too, so the UI never confirms or denies an account.
 */
export function ForgotPassword(): ReactNode {
  const api = usePortalApi();
  const { apiPrefix, authExtra } = usePortalAuthConfig();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const canSubmit = !busy && !!email.trim();

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    const body: Record<string, string> = { ...authExtra, email: email.trim() };
    api<{ ok: true }>(`${apiPrefix}/auth/forgot-password`, { method: "POST", body })
      .catch(() => {
        // The response is deliberately opaque, so the UI shows the same
        // generic success either way.
      })
      .finally(() => {
        setBusy(false);
        setSubmitted(true);
      });
  };

  return (
    <AuthCard
      title="Reset your password"
      subtitle={submitted ? undefined : "Enter your email and we'll send you a reset link."}
      footer={
        <Link to="/login" className="text-primary-text hover:text-ember-dark font-medium">
          Back to sign in
        </Link>
      }
    >
      {submitted ? (
        <p
          role="status"
          className="border-success-border bg-success-bg text-forest rounded-2xl border px-4 py-3 text-sm"
        >
          {GENERIC_SUCCESS}
        </p>
      ) : (
        <form className="space-y-5" onSubmit={onSubmit} noValidate>
          <Input
            label="Email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="submit" size="lg" className="w-full" disabled={!canSubmit}>
            {busy ? "Sending…" : "Send reset link"}
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
